/**
 * Create material modal
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, TreeSelect, Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { materialApi, folderApi } from '../../services/resourceService';
import { ResourceScope, ResourceFolder, FolderType, MaterialType } from '../../types/resource';

const { Option } = Select;
const { Dragger } = Upload;

interface CreateMaterialModalProps {
  visible: boolean;
  scope: ResourceScope;
  onCancel: () => void;
  onSuccess: () => void;
}

const CreateMaterialModal: React.FC<CreateMaterialModalProps> = ({
  visible,
  scope,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);

  // Load folders
  useEffect(() => {
    if (visible) {
      loadFolders();
    }
  }, [visible, scope]);

  const loadFolders = async () => {
    try {
      const result = await folderApi.getTree(FolderType.MATERIAL, scope);
      setFolders(result);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Convert folders to tree data
  const buildTreeData = (folders: ResourceFolder[]): any[] => {
    return folders.map((folder) => ({
      title: folder.name,
      value: folder.id,
      children: folder.children ? buildTreeData(folder.children) : [],
    }));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (fileList.length === 0) {
        message.error('请上传文件');
        return;
      }

      setLoading(true);

      console.log('=== 上传素材 ===');
      console.log('scope 参数:', scope);
      console.log('folder_id:', values.folder_id);
      console.log('name:', values.name);

      // Upload file to COS
      await materialApi.upload(fileList[0], {
        scope: scope,
        folder_id: values.folder_id || undefined,
        name: values.name,
        tags: values.tags,
      });

      message.success('上传成功');
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '上传失败');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file: any) => {
      setFileList([file]);
      form.setFieldsValue({ name: file.name });
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <Modal
      title="上传素材"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item label="文件">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持图片、视频、音频、文档等格式
            </p>
          </Dragger>
        </Form.Item>

        <Form.Item
          name="name"
          label="名称"
          rules={[{ required: true, message: '请输入名称' }]}
        >
          <Input placeholder="请输入素材名称" />
        </Form.Item>

        <Form.Item name="folder_id" label="分组">
          <TreeSelect
            placeholder="请选择分组（可选）"
            allowClear
            treeData={buildTreeData(folders)}
          />
        </Form.Item>

        <Form.Item name="tags" label="标签">
          <Input placeholder="多个标签用逗号分隔" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateMaterialModal;
